import {
  parseReferralRewardType,
  parseReferralTrackingStatus,
  parseReferralTrigger,
  buildUpsertReferralRulePayload,
  defaultReferralRuleUpsertTrigger,
  isReferralRuleUpsertTriggerSupported,
} from "@/api/dataSource/api/Spirit/Admin/Interfaces/Models/Referral/referralRuleMapper";

export {
  parseReferralRewardType,
  parseReferralTrackingStatus,
  parseReferralTrigger,
  buildUpsertReferralRulePayload,
  defaultReferralRuleUpsertTrigger,
  isReferralRuleUpsertTriggerSupported,
};

export interface ReferralSelectOption {
  id: number | boolean | null;
  name: string;
}

export interface ReferralTriggerDefinition {
  id: number;
  name: string;
  description: string;
  thresholdLabel: string;
  thresholdHint: string;
  windowLabel: string;
  windowHint: string;
  defaultThreshold: number;
  defaultWindowDays: number | null;
  usesThreshold: boolean;
  usesWindow: boolean;
}

export interface ReferralRewardDefinition {
  id: number;
  name: string;
  description: string;
  usesRewardDays: boolean;
}

const referralTriggerDefinitions: ReferralTriggerDefinition[] = [
  {
    id: 0,
    name: "Приглашённый получил Премиум",
    description:
      "Премиум начисляется пригласившему, когда приглашённый пользователь получил Премиум.",
    thresholdLabel: "Порог",
    thresholdHint: "Не используется для этого типа.",
    windowLabel: "Срок, дней",
    windowHint: "Не используется для этого типа.",
    defaultThreshold: 1,
    defaultWindowDays: null,
    usesThreshold: false,
    usesWindow: false,
  },
  {
    id: 1,
    name: "Приглашённый купил Премиум",
    description:
      "Премиум начисляется пригласившему, когда приглашённый оформил Премиум.",
    thresholdLabel: "Порог",
    thresholdHint: "Не используется для этого типа.",
    windowLabel: "Срок, дней",
    windowHint: "Не используется для этого типа.",
    defaultThreshold: 1,
    defaultWindowDays: null,
    usesThreshold: false,
    usesWindow: false,
  },
  {
    id: 2,
    name: "N приглашённых оформили Премиум",
    description:
      "Премиум начисляется пригласившему, когда указанное число приглашённых оформили Премиум.",
    thresholdLabel: "Количество приглашённых",
    thresholdHint: "Сколько приглашённых должны оформить Премиум.",
    windowLabel: "Период, дней",
    windowHint: "Не используется для этого типа.",
    defaultThreshold: 1,
    defaultWindowDays: null,
    usesThreshold: true,
    usesWindow: false,
  },
  {
    id: 3,
    name: "N приглашённых оформили Премиум за период",
    description:
      "Премиум начисляется пригласившему, когда указанное число приглашённых оформили Премиум за заданный период.",
    thresholdLabel: "Количество приглашённых",
    thresholdHint: "Сколько приглашённых должны оформить Премиум.",
    windowLabel: "Период, дней",
    windowHint: "За сколько дней должны оформить Премиум указанное число приглашённых.",
    defaultThreshold: 1,
    defaultWindowDays: 30,
    usesThreshold: true,
    usesWindow: true,
  },
  {
    id: 4,
    name: "N дней подряд занятий",
    description:
      "Премиум начисляется пригласившему за streak — N дней подряд занятий.",
    thresholdLabel: "Количество дней подряд",
    thresholdHint: "Сколько дней подряд нужно заниматься.",
    windowLabel: "Срок, дней",
    windowHint: "Не используется для этого типа.",
    defaultThreshold: 7,
    defaultWindowDays: null,
    usesThreshold: true,
    usesWindow: false,
  },
];

const referralRewardDefinitions: ReferralRewardDefinition[] = [
  {
    id: 0,
    name: "Дни Премиум",
    description: "Фиксированное число дней Премиума для пригласившего.",
    usesRewardDays: true,
  },
  {
    id: 1,
    name: "Месяц Премиум",
    description: "Один месяц Премиума для пригласившего.",
    usesRewardDays: false,
  },
  {
    id: 2,
    name: "Год Премиум",
    description: "Один год Премиума для пригласившего.",
    usesRewardDays: false,
  },
  {
    id: 3,
    name: "Пожизненный Премиум",
    description: "Пожизненный Премиум для пригласившего.",
    usesRewardDays: false,
  },
];

const referralTrackingStatusDefinitions: Array<{ id: number; name: string; description: string }> = [
  {
    id: 0,
    name: "Ожидает условие",
    description: "Условие правила ещё не выполнено, премиум не начислен.",
  },
  {
    id: 1,
    name: "Премиум начислен",
    description: "Условие выполнено, награда выдана пригласившему.",
  },
  {
    id: 2,
    name: "Отменено",
    description: "Запись отменена, начисление не будет произведено.",
  },
];

export const referralTriggerOptions: ReferralSelectOption[] = [
  { id: null, name: "Любой тип условия" },
  ...referralTriggerDefinitions.map(({ id, name }) => ({ id, name })),
];

export const referralRuleUpsertTriggerOptions: ReferralSelectOption[] =
  referralTriggerDefinitions
    .filter(({ id }) => isReferralRuleUpsertTriggerSupported(id))
    .map(({ id, name }) => ({ id, name }));

export const referralRewardTypeOptions: ReferralSelectOption[] =
  referralRewardDefinitions.map(({ id, name }) => ({ id, name }));

export const referralTrackingStatusOptions: ReferralSelectOption[] = [
  { id: null, name: "Любой статус" },
  ...referralTrackingStatusDefinitions.map(({ id, name }) => ({ id, name })),
];

export const referralActivityOptions: ReferralSelectOption[] = [
  { id: null, name: "Все правила" },
  { id: true, name: "Только включённые" },
  { id: false, name: "Только выключенные" },
];

export function getTriggerDefinition(trigger: any): ReferralTriggerDefinition | null {
  if (trigger === null || trigger === undefined) {
    return null;
  }
  const id = parseReferralTrigger(trigger);
  if (id === null) {
    return null;
  }
  return referralTriggerDefinitions.find((item) => item.id === id) || null;
}

export function getRewardDefinition(rewardType: any): ReferralRewardDefinition | null {
  if (rewardType === null || rewardType === undefined) {
    return null;
  }
  const id = parseReferralRewardType(rewardType);
  if (id === null) {
    return null;
  }
  return referralRewardDefinitions.find((item) => item.id === id) || null;
}

function parseEnumId(value: any): number | string | null {
  if (value === null || value === undefined || value === "") {
    return null;
  }

  if (typeof value === "number" && !Number.isNaN(value)) {
    return value;
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    const asNumber = Number(trimmed);
    if (!Number.isNaN(asNumber) && /^-?\d+$/.test(trimmed)) {
      return asNumber;
    }

    const triggerId = parseReferralTrigger(value);
    if (triggerId !== null) {
      return triggerId;
    }

    const rewardTypeId = parseReferralRewardType(value);
    if (rewardTypeId !== null) {
      return rewardTypeId;
    }

    const statusId = parseReferralTrackingStatus(value);
    if (statusId !== null) {
      return statusId;
    }

    return value;
  }

  return value;
}

export function getOptionName(options: ReferralSelectOption[], id: any): string {
  const parsedId = parseEnumId(id);
  if (parsedId === null) {
    return "-";
  }

  const option = options.find((item) => item.id == parsedId);
  return option ? option.name : "-";
}

export const referralRulesTableHeaders = {
  threshold: "Порог условия",
  windowDays: "Срок, дней",
  rewardType: "Тип награды",
  rewardDays: "Дней премиума",
};
